import { useQuery } from "@tanstack/react-query";

import { getRanaApiV1 } from "@nens/rana/api-v1";

type GitHubActionStatus =
  // Success
  | "success"
  // In progress
  | "queued"
  | "waiting"
  | "requested"
  | "pending"
  | "running"
  // Failed
  | "failure"
  // Warning
  | "cancelled"
  | "action_required"
  | "timed_out"
  // Info
  | "skipped"
  | "neutral"
  | "stale";

export const e2eStatusToAlertStatus = <T extends GitHubActionStatus>(status: T) => {
  switch (status) {
    case "success":
      return "success";
    case "failure":
      return "error";
    case "queued":
    case "waiting":
    case "requested":
    case "pending":
    case "running":
      return "inProgress";
    case "cancelled":
    case "action_required":
    case "timed_out":
      return "warning";
    case "neutral":
    case "skipped":
    case "stale":
      return "info";
    default:
      console.warn("Unknown e2e status:", status);
      return "warning";
  }
};

export type E2EStatus = {
  status: GitHubActionStatus;
  datetime: Date;
  runUrl: string;
  runId: number;
  summary: string;
  frontendVersion: string;
  apiVersion: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function E2EStatusFromJSONTyped(json: any): E2EStatus {
  if (json == null) {
    return json;
  }
  return {
    status: json["status"],
    datetime: new Date(json["datetime"]),
    runUrl: json["run_url"],
    runId: json["run_id"],
    summary: json["summary"],
    frontendVersion: json["frontend_version"],
    apiVersion: json["api_version"],
  };
}

const queryE2EStatus = () => {
  return getRanaApiV1().getQueryOptions({
    ignoreTenant: true,
    queryKey: ["e2e-status"],
    queryFn: async () => {
      const urlPath = `/e2e_status.json`;

      const response = await fetch(urlPath, {
        method: "GET",
        headers: {
          Authorization: getRanaApiV1().accessToken ?? "",
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to e2e status: ${response.status} ${response.statusText}`);
      }

      return E2EStatusFromJSONTyped(await response.json());
    },
    refetchInterval: (q) => {
      const status = q.state.data?.status;
      // If the status is in progress, we want to check more frequently for updates, otherwise we can check less frequently.
      if (status && e2eStatusToAlertStatus(status) === "inProgress") return 30_000;
      return 60_000 * 5;
    },
  });
};

export const useE2EStatusQuery = () => {
  return useQuery(queryE2EStatus());
};
